import { SynqoHeader, SynqoSidebar } from '@Components/Navigation';
import { LOGIN_REQUEST } from '@Constants/authorizationConstants';
import {
    ASPIRE_HOSTED,
    OTEL_EXPORTER_OTLP_ENDPOINT,
    OTEL_EXPORTER_OTLP_HEADERS,
    OTEL_RESOURCE_ATTRIBUTES,
} from '@Constants/runtimeConstants';
import { AppStateProvider } from '@Contexts/appContext';
import { DocumentsProvider } from '@Contexts/documents';
import { NavigationProvider, NavigationUpdater } from '@Contexts/navigation';
import { NotificationHubProvider } from '@Contexts/notificationHub';
import { NotificationsCountProvider } from '@Contexts/notificationsCount';
import { LegalDocumentAcceptanceGuard } from '@Contexts/session/LegalDocumentAcceptanceGuard';
import { FrontendLayoutSettingsProvider } from '@Contexts/settings';
import { SignalProvider } from '@Contexts/signal';
import { useStoredToken } from '@Hooks/useStoredToken';
import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, useIsAuthenticated } from '@azure/msal-react';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import {
    AppShell,
    commaSeperatedKeyValuePairsToRecord,
    OnTheFlyModalProvider,
    type OtlpConfig,
    TelemetryProvider,
    useToken,
} from '@synqo/frontend-component-library';
import { type PropsWithChildren, useCallback, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router';

const otlpConfig: OtlpConfig | undefined = ASPIRE_HOSTED
    ? {
          exporterEndpoint: OTEL_EXPORTER_OTLP_ENDPOINT,
          exporterHeaders: commaSeperatedKeyValuePairsToRecord(OTEL_EXPORTER_OTLP_HEADERS),
          resourceAttributes: buildOtlpResourceAttributes(),
          propagateTraceHeaderCorsUrls: new RegExp('/.*/'),
      }
    : undefined;

export default function App() {
    const isAuthenticated = useIsAuthenticated();
    const { getToken } = useToken();
    const { setToken } = useStoredToken();

    useEffect(() => {
        if (isAuthenticated && ASPIRE_HOSTED) {
            getToken().then(setToken);
        }
    }, [isAuthenticated, getToken, setToken]);

    const errorComponent = useCallback(() => <Navigate to="/" replace />, []);

    return (
        <TelemetryProvider otlp={otlpConfig}>
            <MsalAuthenticationTemplate
                authenticationRequest={LOGIN_REQUEST}
                interactionType={InteractionType.Redirect}
                errorComponent={errorComponent}
            >
                <SynqoCloudAppProviders>
                    <Outlet />
                    <NavigationUpdater />
                </SynqoCloudAppProviders>
            </MsalAuthenticationTemplate>
        </TelemetryProvider>
    );
}

function SynqoCloudAppProviders({ children }: PropsWithChildren) {
    return (
        <SignalProvider>
            <AppStateProvider>
                <LegalDocumentAcceptanceGuard>
                    <NotificationsCountProvider>
                        <NotificationHubProvider>
                            <FrontendLayoutSettingsProvider>
                                <DocumentsProvider>
                                    <OnTheFlyModalProvider>
                                        <NavigationProvider>
                                            <AppShell header={<SynqoHeader />} sidebar={<SynqoSidebar />}>
                                                {children}
                                            </AppShell>
                                        </NavigationProvider>
                                    </OnTheFlyModalProvider>
                                </DocumentsProvider>
                            </FrontendLayoutSettingsProvider>
                        </NotificationHubProvider>
                    </NotificationsCountProvider>
                </LegalDocumentAcceptanceGuard>
            </AppStateProvider>
        </SignalProvider>
    );
}

function buildOtlpResourceAttributes(): Record<string, string> {
    const attributes = commaSeperatedKeyValuePairsToRecord(OTEL_RESOURCE_ATTRIBUTES);
    attributes[ATTR_SERVICE_NAME] = 'frontend';
    return attributes;
}
